import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookedEvents, Event } from "../../types/Types";
import { EventListCard } from "./EventListCard";
import type { EventsListMode } from "./eventsListShared";
import "../css/EventFormatCarousel.css";

/** Desktop: 3 columns × 2 rows per page. */
const PAGE_SIZE = 6;

function chunkEvents(list: Event[], size: number): Event[][] {
  if (list.length === 0) return [];
  const pages: Event[][] = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages;
}

export const EventFormatCarousel = memo(function EventFormatCarousel({
  title,
  icon,
  events,
  listMode,
  bookedLookup,
  savedIds,
  onToggleFavorite,
  onBook,
  onViewBooked,
  onViewAttendees,
  getBookedEvent,
}: {
  title: string;
  icon: string;
  events: Event[];
  listMode: EventsListMode;
  bookedLookup: Map<number, BookedEvents> | null;
  savedIds: Set<number>;
  onToggleFavorite: (event: Event) => void;
  onBook: (eventId: number) => void;
  onViewBooked: (event: BookedEvents) => void;
  onViewAttendees: (eventId: number) => void;
  getBookedEvent: (eventId: number) => BookedEvents | undefined;
}) {
  const { t } = useTranslation();
  const pages = useMemo(() => chunkEvents(events, PAGE_SIZE), [events]);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
  }, [events]);

  useEffect(() => {
    if (pageIndex > 0 && pageIndex >= pages.length) {
      setPageIndex(Math.max(0, pages.length - 1));
    }
  }, [pageIndex, pages.length]);

  const pageEvents = pages[pageIndex] ?? [];
  const totalPages = pages.length;
  const canPrev = pageIndex > 0;
  const canNext = pageIndex < totalPages - 1;

  if (events.length === 0) {
    return null;
  }

  return (
    <section className="event-format-carousel" aria-label={title}>
      <header className="event-format-carousel__header">
        <span className="event-format-carousel__icon" aria-hidden>
          <i className={`bi ${icon}`} />
        </span>
        <h2 className="event-format-carousel__title">{title}</h2>
        <span className="event-format-carousel__rule" aria-hidden />
      </header>

      <div className="event-format-carousel__frame">
        <div
          className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 align-items-stretch"
          key={pageIndex}
        >
          {pageEvents.map((event) => (
            <div className="col d-flex" key={event.id}>
              <EventListCard
                event={event}
                listMode={listMode}
                isBooked={
                  listMode === "attended"
                    ? true
                    : !!bookedLookup?.has(event.id)
                }
                isFavorite={savedIds.has(event.id)}
                onToggleFavorite={onToggleFavorite}
                onBook={onBook}
                onViewBooked={onViewBooked}
                onViewAttendees={onViewAttendees}
                getBookedEvent={getBookedEvent}
              />
            </div>
          ))}
        </div>

        {totalPages > 1 ? (
          <div className="event-format-carousel__nav">
            <button
              type="button"
              className="event-format-carousel__btn"
              aria-label={t("events.carouselPrev")}
              disabled={!canPrev}
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            >
              <i className="bi bi-chevron-left" aria-hidden />
            </button>
            <span className="event-format-carousel__page" aria-live="polite">
              {pageIndex + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="event-format-carousel__btn"
              aria-label={t("events.carouselNext")}
              disabled={!canNext}
              onClick={() =>
                setPageIndex((p) => Math.min(totalPages - 1, p + 1))
              }
            >
              <i className="bi bi-chevron-right" aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
});

EventFormatCarousel.displayName = "EventFormatCarousel";
